import React from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import {
  Header,
  Button,
  Loader,
  Stack,
  Text,
  Select,
  Label,
} from '@crossfit/ui'

import type { Teammate, MyAffiliate } from 'types'
import { useNotification } from 'components/Notification'
import * as affiliatesService from 'services/affiliates'
import Modal from 'components/Modal'

type Props = {
  onClose: () => void
  teammates: Teammate[]
  affiliate: MyAffiliate
}

function useAssignManagerForm({ onClose, teammates, affiliate }: Props) {
  const queryClient = useQueryClient()
  const form = useForm()
  const showNotification = useNotification()
  const assignManager = useMutation(
    (data: Parameters<typeof affiliatesService.assignManager>[0]) =>
      affiliatesService.assignManager(data),
  )

  async function handleSubmit(data) {
    const userId = Number(data.userId)
    const { firstName, lastName } = (teammates ?? []).find(
      (teammate) => teammate.id === userId,
    )

    await assignManager.mutateAsync({
      affiliateId: affiliate?.affiliateId,
      userId,
    })
    await queryClient.refetchQueries('members')
    showNotification(
      `${firstName} ${lastName} has successfully been assigned as Manager`,
    )
    onClose()
  }

  return {
    ...form,
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting: form.formState.isSubmitting,
  }
}

export function AssignManagerModal({ onClose, teammates, affiliate }: Props) {
  const { onSubmit, isSubmitting, register, watch } = useAssignManagerForm({
    onClose,
    teammates,
    affiliate,
  })

  return (
    <Modal
      onClose={onClose}
      header={
        <Header size='small' as='h3'>
          Assign Manager
        </Header>
      }
    >
      {isSubmitting ? (
        <Loader />
      ) : (
        <>
          <Text mb={8}>
            Select the individual you would like to assign as Manager of your
            team.
          </Text>

          <form onSubmit={onSubmit}>
            <Stack spacing={2} mb={12} style={{ alignItems: 'flex-start' }}>
              <Label htmlFor='userId'>Member</Label>
              <Select id='userId' {...register('userId')}>
                <option disabled selected>
                  Select a team member
                </option>
                {teammates.map((teammate) => (
                  <option key={teammate.id} value={teammate.id}>
                    {teammate.firstName} {teammate.lastName}
                  </option>
                ))}
              </Select>
            </Stack>

            <Stack
              direction='horizontal'
              style={{ justifyContent: 'flex-end' }}
              spacing={4}
            >
              <Button type='button' onClick={onClose} variant='secondary'>
                Cancel
              </Button>
              <Button type='submit' disabled={!watch('userId')}>
                Assign Manager
              </Button>
            </Stack>
          </form>
        </>
      )}
    </Modal>
  )
}

export default AssignManagerModal
